/**
 * Vite app providers
 *
 * Provider order (outermost → innermost):
 *   ClerkProvider      → auth session + JWT
 *   ConvexProvider     → real-time DB client (consumes Clerk JWT via ConvexProviderWithClerk)
 *   IntlProvider       → client-side i18n
 *   ThemeProvider      → next-themes
 *   AuthInitializer    → bootstraps Zustand stores on auth (migrating from Supabase → Clerk)
 *
 * NOTE: Runtime components that use router hooks (MiniTimer, AutoStartHandler,
 * TimerRuntime, NotificationRuntime) live in the router's root route — NOT here.
 * This tree wraps RouterProvider, so router context does not exist at this level;
 * mounting router-hook consumers here crashes the app on boot.
 */
import { ClerkProvider, useAuth } from '@clerk/clerk-react'
import { deDE, enUS } from '@clerk/localizations'
import { ConvexProviderWithClerk } from 'convex/react-clerk'
import { convex } from '@/lib/convex-client'
import { ThemeProvider } from 'next-themes'
import { AuthInitializer } from '@/stores/auth-initializer'
import { ClerkAuthBridge } from './clerk-auth-bridge'
import { PerformanceMonitor } from '@/components/utils/performance-monitor'
import { Toaster } from '@/components/ui/sonner'
import { IntlProvider } from '@/lib/i18n-client'

interface ViteAppProvidersProps {
  children: React.ReactNode
}

/**
 * Clerk's own UI (sign-in, sign-up, the account modal) ships its own strings, so
 * it needs the locale too — otherwise a German user meets English forms. Read
 * from the cookie the language switcher writes, since ClerkProvider sits above
 * IntlProvider and cannot use the i18n context.
 */
/**
 * Clerk UI styled from the app's own design tokens.
 *
 * The values are the same CSS variables the rest of the app uses, so Clerk's
 * screens follow the active theme (including the light/dark switch) instead of
 * carrying their own palette.
 */
const clerkAppearance = {
  variables: {
    colorPrimary: 'hsl(var(--primary))',
    colorBackground: 'hsl(var(--background))',
    colorText: 'hsl(var(--foreground))',
    colorTextSecondary: 'hsl(var(--muted-foreground))',
    colorInputBackground: 'hsl(var(--background))',
    colorInputText: 'hsl(var(--foreground))',
    colorDanger: 'hsl(var(--destructive))',
    colorNeutral: 'hsl(var(--foreground))',
    borderRadius: 'var(--radius)',
    fontFamily: 'inherit',
  },
  elements: {
    card: 'shadow-none border border-border',
    cardBox: 'shadow-none',
    formButtonPrimary: 'text-sm normal-case',
  },
} as const

function clerkLocalization() {
  const locale = document.cookie
    .split('; ')
    .find((row) => row.startsWith('locale='))
    ?.split('=')[1]

  return locale === 'de' ? deDE : enUS
}

export function ViteAppProviders({ children }: ViteAppProvidersProps) {
  return (
    <ClerkProvider
      publishableKey={import.meta.env.VITE_CLERK_PUBLISHABLE_KEY as string}
      localization={clerkLocalization()}
      appearance={clerkAppearance}
    >
      <ConvexProviderWithClerk client={convex} useAuth={useAuth}>
        <ClerkAuthBridge />
        <IntlProvider>
          <ThemeProvider attribute="class" defaultTheme="dark" enableSystem disableTransitionOnChange>
            <AuthInitializer>
              {children}
              <PerformanceMonitor />
            </AuthInitializer>
          </ThemeProvider>
          <Toaster richColors position="bottom-right" />
        </IntlProvider>
      </ConvexProviderWithClerk>
    </ClerkProvider>
  )
}
